package org.gstechnovos.jexonplastech.sections.about.sections

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.TextTransform
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.gstechnovos.jexonplastech.sections.about.styles.CapabilitiesContainerStyle
import org.gstechnovos.jexonplastech.sections.about.styles.CapabilityItemStyle
import org.gstechnovos.jexonplastech.theme.toSitePalette
import org.gstechnovos.jexonplastech.theme.unextractable
import org.gstechnovos.jexonplastech.util.*
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div

// ==========================================
// CMS DATA MODEL
// ==========================================
/**
 *
 * Images:
 * Main Facility Image: Renders with a minimum height of 400px and uses ObjectFit.Cover to fill the space.
 * Constraint: Upload a landscape or square image (e.g., 800x600px or 800x800px minimum)
 * so it doesn't lose quality when stretched.
 *
 * Capability Icons: Hardcoded to render at exactly 24x24px.
 * Constraint: Use a 1:1 square aspect ratio (e.g., SVG or 64x64px PNG) to prevent distortion.
 *
 * Text:
 * Overline: Renders at 14px in uppercase. Limit to 2–4 words (e.g., "FACILITY & INFRASTRUCTURE").
 * Headline: Renders at 36px on desktop and 28px on mobile. Limit to 3–6 words.
 * Capability Titles: Renders at 18px. Limit to 2–3 words (e.g., "Modern Factory").
 * Capability Descriptions: Renders at 15px. Limit to 2–3 sentences to keep the 2x2 grid heights even.
 *
 */
data class CapabilityItem(
    val title: String, val description: String, val iconPath: String
)

data class AboutCapabilitiesData(
    val overline: String, val headline: String, val imagePath: String, val capabilities: List<CapabilityItem>
)

val defaultAboutCapabilitiesData = AboutCapabilitiesData(
    overline = "FACILITY & INFRASTRUCTURE",
    headline = "State-of-the-Art Manufacturing",
    imagePath = Res.Image.Home.Overview.HOME_OVERVIEW, // Reusing facility image for now
    capabilities = listOf(
        CapabilityItem(
            title = "Modern Factory",
            description = "A massive, fully optimized production facility built to handle continuous, high-volume manufacturing cycles.",
            iconPath = Res.Image.Home.Manufacturing.PRODUCTION
        ), CapabilityItem(
            title = "Advanced Machinery",
            description = "Utilizing the latest injection molding and bristle-tufting technology for precise, defect-free products.",
            iconPath = Res.Image.About.INFRASTRUCTURE.MACHINERY
        ), CapabilityItem(
            title = "Quality Control",
            description = "Rigorous multi-stage inspections guarantee every batch meets strict international B2B standards.",
            iconPath = Res.Image.About.Mission.QUALITY
        ), CapabilityItem(
            title = "Production Scale",
            description = "Capable of fulfilling massive vendor orders with rapid turnaround times and consistent stock availability.",
            iconPath = Res.Image.About.INFRASTRUCTURE.PRODUCTION
        )
    )
)

// ==========================================
// COMPONENT
// ==========================================

@Composable
fun AboutCapabilitiesSection(data: AboutCapabilitiesData = defaultAboutCapabilitiesData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val palette = ColorMode.current.toSitePalette()

    val (isVisible, visibilityRef) = rememberScrollVisibility(threshold = 0.2)

    Box(
        modifier = CapabilitiesContainerStyle.toModifier().padding(
            topBottom = if (isDesktop) 100.px else 60.px, leftRight = 5.percent
        ), contentAlignment = Alignment.Center
    ) {
        Div(attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex).justifyContent(JustifyContent.Center).toAttrs {
            ref { element ->
                visibilityRef(element)
                onDispose {}
            }
        }) {

            SimpleGrid(
                numColumns(1, lg = 2),
                modifier = Modifier.fillMaxWidth().maxWidth(1200.px).gap(if (isDesktop) 60.px else 40.px)
            ) {
                // --- LEFT COLUMN: Facility Image (Slides in from left) ---
                Box(
                    modifier = Modifier.fillMaxWidth().height(100.percent).minHeight(400.px)
                        .animateSlideInLeft(isVisible, delayMs = 0), contentAlignment = Alignment.Center
                ) {
                    Image(
                        src = data.imagePath,
                        alt = "Jexon Plastech Facility",
                        modifier = Modifier.fillMaxSize().objectFit(ObjectFit.Cover).borderRadius(16.px)
                            .boxShadow(0.px, 20.px, 40.px, color = palette.custom.darkText.toRgb().copyf(alpha = 0.1f))
                            .unextractable()
                    )
                }

                // --- RIGHT COLUMN: Details & Grid (Slides in from right) ---
                Column(
                    modifier = Modifier.fillMaxWidth().animateSlideInRight(isVisible, delayMs = 200),
                    verticalArrangement = Arrangement.Center
                ) {
                    SpanText(
                        text = data.overline,
                        modifier = Modifier.color(palette.brand.primaryBlue).fontSize(14.px).fontWeight(FontWeight.Bold)
                            .letterSpacing(1.5.px).textTransform(TextTransform.Uppercase).margin(bottom = 12.px)
                    )

                    SpanText(
                        text = data.headline,
                        modifier = Modifier.color(palette.custom.darkText).fontSize(if (isDesktop) 36.px else 28.px)
                            .fontWeight(FontWeight.Bold).lineHeight(1.2).margin(bottom = 32.px)
                    )

                    // 2x2 Grid for the specific capabilities
                    SimpleGrid(
                        numColumns(1, sm = 2), modifier = Modifier.fillMaxWidth().gap(30.px)
                    ) {
                        data.capabilities.forEach { capability ->
                            CapabilityBlock(capability)
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun CapabilityBlock(item: CapabilityItem) {
    val palette = ColorMode.current.toSitePalette()

    Column(modifier = CapabilityItemStyle.toModifier(), horizontalAlignment = Alignment.Start) {
        Row(
            verticalAlignment = Alignment.CenterVertically, modifier = Modifier.margin(bottom = 12.px).gap(12.px)
        ) {
            Image(
                src = item.iconPath, modifier = Modifier.size(24.px).unextractable()
            )
            SpanText(
                text = item.title,
                modifier = Modifier.color(palette.custom.darkText).fontSize(18.px).fontWeight(FontWeight.Bold)
            )
        }

        SpanText(
            text = item.description,
            modifier = Modifier.color(palette.custom.darkText.toRgb().copyf(alpha = 0.7f)).fontSize(15.px)
                .lineHeight(1.6)
        )
    }
}